// Source issues (ADR-031; contracts 1.4): a question for the guide's owners about their own guide — a
// contradiction, a silence, a probable typo, or something the intake pipeline found — and the answer
// a client reviewer gives inside the product. The register (content/<app>/review/guide-issues.yaml)
// stays the source of the questions; the product holds the answers. `summary` is our paraphrase of
// the question, never guide text. Only `open` is read by the gates (render, retrieval, promotion).
import { z } from "zod";
import { Count, Id, IsoUtc, NonEmpty, strictObject, version } from "./primitives.js";
export const SourceIssueKind = z.enum([
    "contradiction",
    "silence",
    "probable-typo",
    "pipeline-finding",
]);
/** Shown in the queue as "from the breakdown" or "found by the pipeline". Separate from kind. */
export const SourceIssueOrigin = z.enum(["breakdown", "pipeline"]);
/** `open` is the only state the gates read; an answered or waived issue lifts every gate. */
export const SourceIssueStatus = z.enum(["open", "answered", "waived"]);
/**
 * One cited printed page, or one range of them: `p.20` or `pp.328–332`, the single-entry forms of the
 * Pages grammar (PAGES_RE). A register entry's list (`pp.20, 38`) is one SourceIssuePage per item, so
 * a reading can name exactly one of them.
 */
export const ONE_PAGE_RE = /^(p\.\d+|pp\.\d+–\d+)$/;
export const SourceIssuePageRef = z
    .string()
    .regex(ONE_PAGE_RE, "expected one page or one range: p.20 or pp.11–13, not a list");
/** `provisional` marks a page numbered by an override (e.g. CAIP's unnumbered PDF pages 342–346). */
export const SourceIssuePage = strictObject({
    page: SourceIssuePageRef,
    provisional: z.literal(true).optional(),
});
/**
 * Broadridge's answer: one of the issue's cited pages as the correct reading, their own words, or a
 * confirmation of an inferred value (such as provisional page numbers). Discriminated on `form`.
 */
export const SourceIssueAnswer = z.discriminatedUnion("form", [
    strictObject({ form: z.literal("reading"), page: SourceIssuePageRef }),
    strictObject({ form: z.literal("text"), text: NonEmpty }),
    strictObject({ form: z.literal("confirm"), value: NonEmpty }),
]);
export const SourceIssueWaiver = strictObject({
    reason: NonEmpty,
    waivedBy: NonEmpty,
    waivedAt: IsoUtc,
});
/** Appended when our side reopens an answer to ask a follow-up; reopening never records an answer. */
export const SourceIssueFollowUp = strictObject({
    askedBy: NonEmpty,
    askedAt: IsoUtc,
    question: NonEmpty,
});
export const SourceIssue = strictObject({
    v: version(1),
    id: Id, // the register id, e.g. GI-caip-12
    packId: Id,
    guide: Id, // a guides.yaml id
    kind: SourceIssueKind,
    origin: SourceIssueOrigin,
    pages: z.array(SourceIssuePage), // empty where the question cites no page
    segments: z.array(Id),
    summary: NonEmpty, // the register's wording: our paraphrase, never guide text
    status: SourceIssueStatus,
    answer: SourceIssueAnswer.optional(),
    answeredBy: NonEmpty.optional(),
    answeredAt: IsoUtc.optional(),
    waiver: SourceIssueWaiver.optional(),
    followUps: z.array(SourceIssueFollowUp),
}).superRefine((s, ctx) => {
    const seen = new Set();
    s.pages.forEach((p, i) => {
        if (seen.has(p.page))
            ctx.addIssue({
                code: "custom",
                message: `source issue ${s.id}: page ${p.page} is cited once`,
                path: ["pages", i, "page"],
            });
        seen.add(p.page);
    });
    // The answer travels with who gave it and when: all three or none.
    const parts = [s.answer, s.answeredBy, s.answeredAt].filter((x) => x !== undefined).length;
    if (s.status === "answered" && parts < 3)
        ctx.addIssue({
            code: "custom",
            message: `source issue ${s.id}: an answered issue carries answer, answeredBy and answeredAt`,
            path: ["status"],
        });
    else if (parts > 0 && parts < 3)
        ctx.addIssue({
            code: "custom",
            message: `source issue ${s.id}: answer, answeredBy and answeredAt travel together`,
            path: ["answer"],
        });
    if (s.status === "waived" && s.waiver === undefined)
        ctx.addIssue({
            code: "custom",
            message: `source issue ${s.id}: a waived issue carries a waiver with its reason`,
            path: ["waiver"],
        });
    if (s.answer?.form === "reading" && !seen.has(s.answer.page))
        ctx.addIssue({
            code: "custom",
            message: `source issue ${s.id}: the reading names ${s.answer.page}, which the issue does not cite`,
            path: ["answer", "page"],
        });
});
export const SourceIssueSummary = strictObject({
    open: Count,
    blocking: Count, // open contradictions holding at least one render: linked to a segment (T0, 28 Oct 2026)
    answered: Count,
});
/** The counts a queue's summary must carry for these issues. */
export function summarizeSourceIssues(issues) {
    let open = 0;
    let blocking = 0;
    let answered = 0;
    for (const i of issues) {
        if (i.status === "open") {
            open++;
            if (i.kind === "contradiction" && i.segments.length > 0)
                blocking++;
        }
        else if (i.status === "answered")
            answered++;
    }
    return { open, blocking, answered };
}
/** The queue response: the issues and the counts the screen shows at the top. */
export const SourceIssueQueue = strictObject({
    issues: z.array(SourceIssue),
    summary: SourceIssueSummary,
}).superRefine((q, ctx) => {
    const want = summarizeSourceIssues(q.issues);
    for (const key of ["open", "blocking", "answered"])
        if (q.summary[key] !== want[key])
            ctx.addIssue({
                code: "custom",
                message: `source issue queue: summary.${key} is ${String(q.summary[key])} but the issues give ${String(want[key])}`,
                path: ["summary", key],
            });
});
//# sourceMappingURL=source-issue.js.map